import * as React from 'react';
import logo from '../assets/logo.png';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { IUserRegistrationData, UserService } from '../services/UserService';
import { useHistory } from 'react-router';
import { Link, Redirect } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useAuthContext } from '../contexts/AuthContext';

export const RegistrationPage = (props: any) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const history = useHistory();
    const firstNameInput = React.useRef<HTMLInputElement>(null);
    const authContext = useAuthContext();

    React.useLayoutEffect(() => {
        if (firstNameInput.current !== null) {
            firstNameInput.current.focus();
        }
    })

    const validationSchema = yup.object().shape({
        firstName: yup.string().max(50,"Max length is 50").required("Required field"),
        lastName: yup.string().max(50,"Max length is 50").required("Required field"),
        company: yup.string().max(100,"Max length is 100").required("Required field"),
        email: yup.string().email("Not a valid email address").max(256,"Max length is 256").required("Required field"),
        password: yup.string().max(20,"Max length is 20").required("Required field")
            .matches(
                /^.*(?=.{6,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
                "Invalid password"
            ),
        confirmPassword: yup.string().required("Required field")
            .when("password", {
                is: (password: string) => (password && password.length > 0 ? true : false),
                then: yup.string().oneOf([yup.ref("password")], "Passwords do not match")
            })
    })

    return (
        <>
        {authContext.user && <Redirect to="/documentation" />}
        <Formik 
            validationSchema={validationSchema}
            initialValues = {{
                firstName: "",
                lastName: "",
                company: "",
                email: "",
                password: "",
                confirmPassword: ""
            }}
            onSubmit={async (values: any, actions: any) => {
                const userData: IUserRegistrationData = {
                    email: values.email,
                    password: values.password,
                    company: values.company,
                    firstName: values.firstName,
                    lastName: values.lastName
                };
                const response = await UserService.register(userData);
                if (!response.error) {
                    history.push("/confirm-email");
                } else {
                    props.apiError(true);
                    props.showToast(true);
                    props.toastMessage("Oops, something went wrong");
                }
                actions.setSubmitting(false);
            }}
        >
        {({ handleSubmit, handleChange, handleBlur, isSubmitting, errors, touched, values }) => (
        <div>
            <Row className="min-vh-85 justify-content-center">
                <Col xs={12} className="mw-520 align-self-center">
                    <Card className="my-5">
                        <Card.Body className="overflow-hidden">
                            <div className="text-center mb-3">
                                <svg width="40" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.9096 0.116455C8.01799 0.116455 0 8.18027 0 18.1284C0 28.0766 8.01799 36.1404 17.9096 36.1404C27.8012 36.1404 35.8192 28.0766 35.8192 18.1284C35.8192 8.18027 27.8012 0.116455 17.9096 0.116455ZM15.4448 26.6741C18.2104 26.6741 20.5046 27.7545 22.2194 29.5039C22.304 29.5904 22.4381 29.5995 22.5319 29.5236C25.8417 26.8234 27.964 22.7116 27.964 18.0931C27.964 17.932 27.9601 17.7723 27.9536 17.6125C28.6476 16.2952 29.0603 14.8036 29.1046 13.2126C29.128 12.3601 29.0395 11.5312 28.8585 10.7364C28.8299 10.6094 28.6476 10.6172 28.6306 10.7469C28.4783 11.9608 28.0577 13.1118 27.4197 14.1214C27.296 13.6801 27.1554 13.2454 26.9927 12.8224C26.9067 12.5919 26.7336 12.1978 26.6177 11.9385C26.5422 11.7696 26.4627 11.6033 26.369 11.4448C25.1164 9.32869 22.8209 7.91051 20.1986 7.91051C18.476 7.91051 16.9031 8.51681 15.6701 9.53428C15.6701 9.53428 15.6701 9.53428 15.6701 9.53297C14.7652 7.34744 13.1324 5.54165 11.0804 4.4155C13.135 3.37838 15.454 2.79304 17.9096 2.79304C26.3299 2.79304 33.1552 9.65737 33.1552 18.1258C33.1552 26.5943 26.3299 33.4586 17.9096 33.4586C14.2274 33.4586 10.8499 32.1452 8.21459 29.9596C10.4606 27.3236 12.8251 26.6754 15.4448 26.6754V26.6741ZM9.51403 8.23003C9.54138 8.1017 9.72366 8.10956 9.74319 8.23919C10.139 10.9655 12.1962 13.2676 15.0373 13.8228C15.1701 13.849 15.8237 13.9878 16.0243 14.0271C16.8966 14.2013 17.5476 14.9346 17.5724 15.829C17.6075 17.0795 16.437 18.0433 15.2391 17.7081C11.9202 16.7784 9.44242 13.7613 9.34216 10.1157C9.32393 9.46749 9.38643 8.83763 9.51403 8.23134V8.23003ZM18.9747 18.516C19.2885 17.97 20.2377 17.932 21.0944 18.4296C21.9525 18.9272 22.2506 20.0233 22.0801 20.3192C21.9095 20.6165 20.8171 20.9032 19.9603 20.4056C19.1023 19.908 18.6609 19.0621 18.9747 18.516Z" fill="#28B56F"></path>
                                </svg>
                            </div>
                            <h3 className="text-center">Developer Portal</h3>
                            <h6 className="text-center font-regular text-gray mb-4">New User Registration</h6>
                            <Form noValidate onSubmit={handleSubmit}>
                                <Form.Row>
                                    <Form.Group as={Col} controlId="firstName">
                                        <Form.Control 
                                            isInvalid={touched.firstName && !!errors.firstName}
                                            name="firstName"
                                            onBlur={(e: any) => {
                                                if (e.currentTarget.value.length > 0) {
                                                    handleBlur(e);
                                                }
                                            }} 
                                            onChange={handleChange} 
                                            placeholder="First name" 
                                            ref={firstNameInput} 
                                            value={values.firstName}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group as={Col} controlId="lastName">
                                        <Form.Control 
                                            isInvalid={touched.lastName && !!errors.lastName} 
                                            name="lastName" 
                                            onBlur={(e: any) => {
                                                if (e.currentTarget.value.length > 0) {
                                                    handleBlur(e);
                                                }
                                            }} 
                                            onChange={handleChange} 
                                            placeholder="Last name" 
                                            value={values.lastName}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                                    </Form.Group>
                                </Form.Row>
                                <Form.Group controlId="companyInput">
                                    <Form.Control 
                                        isInvalid={touched.company && !!errors.company}
                                        name="company" 
                                        onBlur={(e: any) => {
                                            if (e.currentTarget.value.length > 0) {
                                                handleBlur(e);
                                            }
                                        }} 
                                        onChange={handleChange} 
                                        placeholder="Company name" 
                                        value={values.company} 
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.company}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="emailInput">
                                    <Form.Control 
                                        isInvalid={touched.email && !!errors.email}
                                        name="email" 
                                        onBlur={(e: any) => {
                                            if (e.currentTarget.value.length > 0) {
                                                handleBlur(e);
                                            }
                                        }} 
                                        onChange={handleChange} 
                                        placeholder="Email" 
                                        type="email" 
                                        value={values.email}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Row>
                                    <Form.Group as={Col} xs={6} controlId="passwordInput" className="mb-0">
                                        <Form.Control 
                                            isInvalid={touched.password && !!errors.password}
                                            name="password"
                                            onBlur={(e: any) => {
                                                if (e.currentTarget.value.length > 0) {
                                                    handleBlur(e);
                                                }
                                            }} 
                                            onChange={handleChange}
                                            placeholder="Password"
                                            type={showPassword ? "text" : "password"} 
                                            value={values.password} 
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group as={Col} xs={6} controlId="confirmPassword" className="mb-0">
                                        <Form.Control 
                                            isInvalid={touched.confirmPassword && !!errors.confirmPassword} 
                                            name="confirmPassword" 
                                            onBlur={(e: any) => {
                                                if (e.currentTarget.value.length > 0) {
                                                    handleBlur(e);
                                                }
                                            }} 
                                            onChange={handleChange} 
                                            placeholder="Confirm password"
                                            type={showPassword ? "text" : "password"}  
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.confirmPassword}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Text className="px-1 py-1 mb-2 text-gray">Use 6 or more characters with at least one uppercase, one number and one special character</Form.Text>
                                    <Form.Group as={Col} controlId="showPassword">
                                        <Form.Check checked={showPassword} type="checkbox" label="Show password" 
                                            onChange={e => setShowPassword(e.target.checked)} 
                                        />
                                    </Form.Group>
                                </Form.Row>
                                <Button type="submit" variant="success" className="rounded-pill" disabled={isSubmitting} block>
                                    {isSubmitting ? (
                                        <React.Fragment>
                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                            <span>&nbsp;Creating account...</span>
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment>
                                            <span>Create Account</span>
                                        </React.Fragment>                                       
                                    )}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                    <Link to="/login" className="d-block text-center">Already have an account?</Link>
                </Col>
            </Row>
        </div>
        )}
        </Formik>
        </>
    );
};